/**
 * ImagePreviewOverlay — Full-screen gallery overlay for reviewing captured images.
 * Shows one image at a time with prev/next navigation, delete, and classify actions.
 */

import React, { useState } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import { Button, Icon } from "@amzn/meridian";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import trashToken from "@amzn/meridian-tokens/base/icon/trash"
import PillButton from "src/components/UI/PillButton";
import chevronLeftLargeTokens from "@amzn/meridian-tokens/base/icon/chevron-left-large";
import chevronRightLargeTokens from "@amzn/meridian-tokens/base/icon/chevron-right-large";
import "./classification.scss";

interface ImagePreviewOverlayProps {
    images: string[];
    onClose: () => void;
    onDelete: (index: number) => void;
    onConfirm: () => void;
    confirmButtonText?: string;
}

const ImagePreviewOverlay: React.FC<ImagePreviewOverlayProps> = ({
    images, onClose, onDelete, onConfirm, confirmButtonText = "CLASSIFY",
}) => {
    const [activeIndex, setActiveIndex] = useState(0);

    const handleDelete = () => {
        onDelete(activeIndex);
        // Adjust index if we deleted the last image
        if (activeIndex >= images.length - 1 && activeIndex > 0) {
            setActiveIndex(activeIndex - 1);
        }
    };

    const handlePrev = () => {
        if (activeIndex > 0) setActiveIndex(activeIndex - 1);
    };

    const handleNext = () => {
        if (activeIndex < images.length - 1) setActiveIndex(activeIndex + 1);
    };

    return (
        <Column className="image-preview-overlay pwa-fullscreen-viewport" minHeight="100vh" heights={["fit", "fill"]}>

            {/* Top bar: close + delete */}
            <Row alignmentHorizontal="justify" alignmentVertical="center" spacingInset="600" className="image-preview--close-and-delete">
                <Button type="icon" onClick={onClose}>
                    <Icon tokens={closeSmallTokens} color="inverted" />
                </Button>
                <Button type="icon" onClick={handleDelete} >
                    <Icon tokens={trashToken} color="error" />
                </Button>
            </Row>

            {/* Image display area + classify button — fills remaining space */}
            <Column alignmentHorizontal="center" alignmentVertical="center" spacing="400" spacingInset="300">

                <Row alignmentHorizontal="justify" width="100%">
                    <Button type="icon" onClick={handlePrev} disabled={activeIndex <= 0}>
                        <Icon tokens={chevronLeftLargeTokens} color={activeIndex <= 0 ? "secondary" : "inverted"} />
                    </Button>

                    <img
                        src={images[activeIndex]}
                        alt={`Captured ${activeIndex + 1}`}
                        className="image-preview-overlay__image"
                    />

                    <Button type="icon" onClick={handleNext} disabled={activeIndex >= images.length-1}>
                        <Icon tokens={chevronRightLargeTokens} color={activeIndex >= images.length - 1 ? "secondary" : "inverted"} />
                    </Button>
                </Row>

                <Text color="inverted" type="b300">
                    Image {activeIndex + 1} of {images.length}
                </Text>

                <Row spacingInset="500" alignmentHorizontal="center">
                    <PillButton text={confirmButtonText} color="success" onClick={onConfirm} />
                </Row>
            </Column>
        </Column>
    );
};

export default ImagePreviewOverlay;
